import React from "react";
import Title from "../../ui/Title";

const PaidCare = () => {
  return (
    <div id="decline-paid-care-workforce" className="scroll-mt-24">
      <Title title="Decline in the paid care workforce" className="mb-5" />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          According to{" "}
          <a
            href="https://www.ilo.org/resource/news/ilo-resolution-decent-work-and-care-economy-moves-ahead"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            estimates
          </a>{" "}
          by the International Labour Organization (ILO), by 2030, more than 2.3
          billion adults and 100 million children will need care services
          globally. As populations age and fertility rates drop, the number of
          carers required will increase. However, a{" "}
          <a
            href="https://www.pnas.org/doi/10.1073/pnas.2315722120"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            2023 paper
          </a>{" "}
          points out that family sizes and networks will shrink. These trends
          will necessitate the need for formal care systems with paid caregivers
          providing support to the growing elderly population. However, the
          number of paid caregivers has been falling across the world, and
          according to the ILO, many countries are struggling to recruit
          long-term care workers. According to{" "}
          <a
            href="https://www.cedefop.europa.eu/en/data-insights/care-workers-skills-opportunities-and-challenges-2023-update"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            data insights
          </a>{" "}
          by Cedefop, a decentralized European agency handling vocational
          education, there will be an estimated 4.3 million positions for care
          workers to be filled in Europe between 2022 and 2035.
        </p>

        <p>
          The{" "}
          <a
            href="https://www.ilo.org/sites/default/files/2024-07/ILC112-Resolution%20V-%7BRELMEETINGS-240620-001%7D-Web-EN_0.pdf"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            ILO’s 2024 resolution
          </a>{" "}
          on decent work and the care economy raised the issue that care work
          ranges from highly-skilled and well-paid work to underpaid work
          carried out in unregulated environments without adequate pay and
          social protection. Migrant and domestic workers, especially women, are
          at risk of being exploited by an economic system which undervalues
          their contribution, and often discriminates against care workers based
          on ethnicity, race, and gender. Limited access to training, career
          growth, and professional development has further weakened the care
          workforce.
        </p>

        <p>
          The Organisation for Economic Cooperation and Development (OECD),
          which is represents largely by high-income and developed countries,
          has highlighted emerging issues of long-term care work in its{" "}
          <a
            href="https://www.oecd.org/content/dam/oecd/en/publications/reports/2023/06/beyond-applause-improving-working-conditions-in-long-term-care_4523ea50/27d33ab3-en.pdf"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            report
          </a>{" "}
          titled,
          <span className="italic">
            Beyond Applause? Improving Working Conditions in Long-Term Care :
          </span>
        </p>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Care work is largely provided by women, and is undervalued and
            underpaid, due to the assumption that anyone can provide care. The
            OECD report cites data from member countries, which shows that 85
            percent of workers in the sector are women. They are not only paid
            less than men in long-term care work but are also underrepresented
            in management positions.
          </li>
          <li>
            Long-term care work is not well-remunerated, with live-in care
            workers earning less than institution-based care workers. The OECD
            report states that long-term care workers earn 12 percent less in
            hourly wages than they would at other jobs. They also lack platforms
            for collective bargaining and participation in decision-making.
          </li>
          <li>
            Even though long-term care work is often physically and mentally
            challenging, care workers frequently lack access to support
            mechanisms.
          </li>
          <li>
            There are limited opportunities for capacity-building and career
            advancement in the field of long-term care.
          </li>
          <li>
            Care work is often provided by migrant laborers from developing
            nations. According to the OECD, 26 percent of long-term care workers
            in the OECD member nations are foreign-born. As many of them enter
            the country through informal channels, it is difficult to monitor
            their working conditions and prevent exploitation.
          </li>
        </ul>

        <h3 className="text-xl font-bold text-black !mt-6 mb-3">
          What do we mean by paid caregiving?
        </h3>

        <p>
          Paid caregiving is not clearly defined, and often, the definitions
          include nurses, administrative staff at elder care facilities, and
          workers in education. The term ‘long-term care’ has therefore been
          used, as current discourse largely trends towards discussing a
          shortage in paid caregivers providing support to the elderly. However,
          even the definition of long-term care tends to vary from source to
          source.
        </p>

        <p>
          The OECD refers to formal long-term care involving the support of
          nurses, ‘personal care workers’, as well as live-in care staff. In its
          2024 report on improving conditions in long-term care, it defines
          formal-long term care as medical, personal care, and assistance
          services which alleviate pain, reduce the deterioration of health
          status among people with long-term dependency, assist in activities of
          daily living, and help people live independently. The report states
          that 78 percent of long-term care workers in OECD countries are not
          qualified or certified as nurses. With the rising older population,
          the report notes an increase in the incidence of complex health issues
          such as dementia and comorbidities. Paid long-term care staff
          therefore need to develop both clinical and communication skills, as
          well as digital and management skills.
        </p>

        <h3 className="text-xl font-bold text-black !mt-6 mb-3">
          Recommendations for improving the conditions of paid caregivers
        </h3>

        <p>
          The WHO issued a{" "}
          <a
            href="https://iris.who.int/bitstream/handle/10665/373347/9789240073852-eng.pdf?sequence=1"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            technical guidance compilation
          </a>{" "}
          on the Global Health and Care Workers Compact in 2023, which outlines
          a series of principles to protect health and care workers. These
          include:
        </p>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Respecting, protecting and fulfilling the human rights of health and
            care workers
          </li>
          <li>
            Ensuring the participation of all health and care workers,
            especially those working with the most marginalized, in developing
            laws, regulations, and policies that affect them
          </li>
          <li>
            Promoting equity in fulfilling commitments to all health and care
            workers
          </li>
          <li>
            Adopting gender-transformative approach which addresses concerns of
            security, equal pay, and discrimination
          </li>
          <li>
            Ensuring transparency and accountability of stakeholders in
            honouring commitments made to health and care workers
          </li>
          <li>
            Facilitating international cooperation in protecting health and care
            workers
          </li>
        </ul>

        <div className="flex flex-col items-center">
          <div className="">
            <img
              src="/assets/img/global-health.png"
              alt="WHO Global Health and Care Workforce Framework for Action"
            />
          </div>
          <p className="text-center">
            Source:{" "}
            <a
              href="https://iris.who.int/bitstream/handle/10665/373347/9789240073852-eng.pdf?sequence=1"
              className="text-blue underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Global Health and Care Workers Compact
            </a>{" "}
            (WHO)
          </p>
        </div>

        <p>
          Recommendations from the ILO, OECD and the United Nations Department
          of Economic and Social Affairs (UN DESA) include a range of measures
          that are rooted in legislation and policies which are designed to
          protect long-term care workers, especially migrants. A{" "}
          <a
            href="https://social.desa.un.org/issues/ageing/news/caregiving-in-an-ageing-world"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            UN DESA brief
          </a>{" "}
          adds that transitioning long-term care work from the informal to a
          more formal structure would make the care sector a more attractive job
          prospect and increase the supply of labor for long-term care.
        </p>

        <p>
          Investing in long-term care infrastructure, systems, and policies has
          the potential to not only increase the recruitment and retention of
          workers, but also promote gender equality, according to{" "}
          <a
            href="https://iris.who.int/bitstream/handle/10665/379077/9789240100756-eng.pdf?sequence=1#:~:text=%E2%80%93%20Greater%20investments%20in%20formal%20LTC,working%20in%20their%20paid%20jobs"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            a WHO brief
          </a>{" "}
          on long-term care financing. The brief adds that these investments
          allow working-age adults to balance informal caregiving with their
          jobs, preventing them from dropping out of the workforce.
        </p>

        <p>
          Investing in long-term care resources involves a range of actions
          carried out by governments, inter-government partnerships, businesses,
          and within communities. These include:
        </p>

        <ul className="list-disc list-outside space-y-2 ml-6">
          <li>
            Strengthening legislation which protects care workers from
            exploitation, especially women, migrants and ethnic minorities, and
            aligns with international labor standards
          </li>
          <li>
            Instituting migration policies and partnerships to allow the
            transfer of skilled care workers through regular channels, and
            ensure protect their rights
          </li>
          <li>
            Enabling care workers to collectivize and have their voices included
            in all decision making
          </li>
          <li>
            Providing social security and benefits, including disability
            benefits, paid maternity leave without fear of losing employment,
            seniority or social allowances, parental leave, and unemployment
            benefits
          </li>
          <li>
            Strengthening regulations for paid care facilities to ensure better
            quality of care
          </li>
          <li>
            Introducing new technologies to increase the efficiency and
            productivity of care workers and reduce their care burden
          </li>
          <li>
            Supporting the transition of undeclared care workers to formal
            employment through vouchers to purchase declared work, reduction in
            the cost of compliance with legislation, and tax incentives
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PaidCare;
